// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — boot and the screen machine. Owns no game rules.
 *
 * The one structural rule this file exists to enforce is ONE ROOM PER SESSION.
 * A `Net` is created when the player enters a room and lives until they walk
 * back to the menu; every rematch happens INSIDE it via rematch.ts. Leaving and
 * re-joining a room to "reset" hands you back a Trystero room that is midway
 * through being destroyed, the mesh never forms, and both players sit alone in
 * the correct room code forever. Two games in this factory shipped that bug.
 */

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds, type RoundInfo } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { newSeed } from '@ben-gy/game-engine/rng';

import { Game, dailySeed, type BuildingKind, type Ev, type Summary } from './game';
import { DEFAULT_MODE, MODES, modeOf, type Mode } from './modes';
import { createRenderer, type Renderer } from './render';
import { createRace, type Race, type Standing } from './race';
import { startCountdown, type Countdown } from './countdown';
import {
  FLOAT_COLOURS,
  createPlayView,
  mountShell,
  renderAbout,
  renderHelp,
  renderMenu,
  renderResults,
  type PlayView,
} from './ui';
import { PALETTE } from './palette';

const SLUG = 'lastlight';
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

const store = createStore(SLUG);
const sfx = createSfx(store.get('muted', false));

const root = document.getElementById('app');
if (!root) throw new Error('missing #app');
const shell = mountShell(root);

let mode: Mode = modeOf(store.get<string>('mode', DEFAULT_MODE.id));

// ── live session state ──────────────────────────────────────────────────────

let game: Game | null = null;
let renderer: Renderer | null = null;
let play: PlayView | null = null;
let race: Race | null = null;
let countdown: Countdown | null = null;
let raf = 0;
let lastT = 0;
let resultsTimer: ReturnType<typeof setInterval> | undefined;

/** The room, if we are in one. ONE per session — see the file header. */
let net: Net | null = null;
let rounds: Rounds | null = null;
let roomCode = '';
let lastSummary: Summary | null = null;

const bestKey = (m: Mode): string => `best:${m.id}`;

function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function stopLoop(): void {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
}

function teardownRound(): void {
  stopLoop();
  countdown?.cancel();
  countdown = null;
  play?.destroy();
  play = null;
  renderer?.destroy();
  renderer = null;
  race?.destroy();
  race = null;
  game = null;
  if (resultsTimer) clearInterval(resultsTimer);
  resultsTimer = undefined;
}

/** Leave the room entirely. Only ever on the way back to the menu. */
async function leaveRoom(): Promise<void> {
  rounds?.destroy();
  rounds = null;
  const n = net;
  net = null;
  roomCode = '';
  clearRoomInUrl();
  // Awaited: Trystero's teardown is deferred, and a later join of the same room
  // resolves only once it has genuinely let go.
  if (n) await n.leave();
}

// ── screens ─────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardownRound();
  setPlaying(false);
  void leaveRoom();
  renderMenu(shell.view, {
    mode,
    best: store.get(bestKey(mode), 0),
    muted: sfx.muted(),
    onMode: (m) => {
      mode = m;
      store.set('mode', m.id);
      showMenu();
    },
    onSolo: () => startSolo(newSeed()),
    onDaily: () => startSolo(dailySeed()),
    onFriends: () => showRoomEntry(),
    onHelp: () => renderHelp(shell.view, showMenu),
    onAbout: () => renderAbout(shell.view, showMenu),
    onMute: (m) => {
      sfx.setMuted(m);
      store.set('muted', m);
      showMenu();
    },
  });
}

function showRoomEntry(): void {
  teardownRound();
  setPlaying(false);
  shell.view.innerHTML = '';
  createRoomEntry({
    container: shell.view,
    title: 'Play with friends',
    subtitle: 'Everyone races the same map. Start a room, or type a friend’s code.',
    onSubmit: (code, created) => void enterRoom(code, created),
    onCancel: showMenu,
  });
}

// ── solo ────────────────────────────────────────────────────────────────────

function startSolo(seed: number): void {
  teardownRound();
  sfx.unlock();
  beginRound(seed, mode, false, 0);
}

// ── multiplayer ─────────────────────────────────────────────────────────────

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardownRound();
  sfx.unlock();
  await leaveRoom();

  roomCode = normalizeRoomCode(code);
  setRoomInUrl(roomCode);

  // Only the peer that MINTED the code may claim the room. A typed code or a
  // link joins as a guest, so a joiner can never steal a live host.
  net = createNet(
    { appId: roomAppId(SLUG), roomId: roomCode, claimHost: created },
    {
      onHostChange: (_id, isSelf) => {
        race?.setHost(isSelf);
        repaintLobbyish();
      },
      onPeerLeave: (id) => {
        race?.peerLeft(id);
        repaintLobbyish();
      },
      onPeers: () => repaintLobbyish(),
    },
  );

  rounds = createRounds({
    net,
    playerName: myName(),
    minPlayers: MIN_PLAYERS,
    // The HOST's mode is what the room plays, frozen into the start. A setting
    // each peer reads from its own UI is a setting two peers can disagree
    // about — and here that would mean different map sizes on the same seed.
    roundOpts: () => ({ mode: mode.id }),
    onRound: (info) => onRoundStart(info),
    onChange: () => repaintLobbyish(),
  });

  showLobby();
}

function myName(): string {
  const existing = store.get<string>('name', '');
  if (existing) return existing;
  const name = `Settler ${1 + Math.floor(Math.random() * 99)}`;
  store.set('name', name);
  return name;
}

function showLobby(): void {
  if (!net || !rounds) return showMenu();
  teardownRound();
  setPlaying(false);
  shell.view.innerHTML = '';
  createLobby({
    container: shell.view,
    net,
    rounds,
    roomCode,
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    onCancel: showMenu,
  });
}

/** Repaint whichever room-ish screen is showing. Cheap and idempotent. */
function repaintLobbyish(): void {
  if (!rounds) return;
  const state = rounds.state();
  if (state.phase === 'playing' && game && !game.over) return; // mid-run; the HUD owns the screen
  if (lastSummary) return showResults();
  if (!play) showLobby();
}

function onRoundStart(info: RoundInfo): void {
  lastSummary = null;

  if (!info.seated) {
    // Connected mid-round: a spectator, never a dead screen. lobby.ts already
    // renders "round in progress — you're in the next one" with the vote live.
    showLobby();
    return;
  }

  const opts = (info.opts ?? {}) as { mode?: string };
  // Validate off the wire: an unknown id must fall back, never reach the map
  // generator as `undefined`.
  const roundMode = modeOf(opts.mode);
  beginRound(info.seed, roundMode, true, info.round, info);
}

// ── a round ─────────────────────────────────────────────────────────────────

function beginRound(
  seed: number,
  roundMode: Mode,
  multiplayer: boolean,
  round: number,
  info?: RoundInfo,
): void {
  teardownRound();

  const g = new Game({ seed, mode: roundMode });
  game = g;

  if (multiplayer && net && info) {
    race = createRace({
      net,
      round,
      seats: info.players.map((p) => ({ id: p.id, name: p.name })),
      selfId: net.selfId,
      isHost: net.isHost(),
      onChange: () => {
        play?.setStandings(race?.standings() ?? []);
        if (lastSummary) showResults();
      },
      onLadder: () => showResults(),
    });
  }

  const view = createPlayView({
    game: () => g,
    renderer: () => renderer!,
    sfx,
    multiplayer,
    onBuild: doBuild,
    onStoke: doStoke,
    onScout: doScout,
    onEndDay: doEndDay,
    onMenu: showMenu,
  });
  play = view;

  shell.view.innerHTML = '';
  shell.view.appendChild(view.root);
  setPlaying(true);

  renderer = createRenderer(view.canvas, () => game);
  renderer.resize();
  view.refresh();
  view.setStandings(race?.standings() ?? []);

  // Count everyone in before the first day is playable.
  let started = false;
  countdown = startCountdown({
    container: view.root,
    sfx,
    onDone: () => {
      started = true;
      view.toast(`Night ${g.mode.days} is a long way off. Build.`);
    },
  });

  lastT = performance.now();
  const frame = (now: number): void => {
    const dt = Math.min(0.05, (now - lastT) / 1000);
    lastT = now;
    renderer?.draw(dt);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  // Guard every action until the countdown has finished, so nobody who was
  // looking at their screen gets a head start.
  const gate = (): boolean => started && !g.over;
  actionGate = gate;
}

let actionGate: () => boolean = () => true;

function doBuild(kind: BuildingKind, q: number, r: number): void {
  const g = game;
  if (!g || !actionGate()) return;
  const err = g.buildError(kind, q, r);
  if (err) {
    play?.toast(err, 'bad');
    sfx.play('hit');
    return;
  }
  g.build(kind, q, r);
  sfx.play('select');
  renderer?.puff(q, r, PALETTE[kind], 12);
  renderer?.shake(2);
  play?.refresh();
}

function doStoke(): void {
  const g = game;
  if (!g || !actionGate()) return;
  if (!g.canStoke()) {
    play?.toast('Not enough coal to stoke.', 'bad');
    sfx.play('hit');
    return;
  }
  const before = g.warmth;
  g.stoke();
  sfx.play('powerup');
  renderer?.puff(0, 0, PALETTE.generator, 22);
  renderer?.pulse(false);
  if (g.warmth > before) {
    play?.toast(`The ring pushes out to ${g.warmth}.`, 'good');
    renderer?.shake(4);
  }
  play?.refresh();
}

function doScout(q: number, r: number): void {
  const g = game;
  if (!g || !actionGate()) return;
  const err = g.scoutError(q, r);
  if (err) {
    play?.toast(err, 'bad');
    sfx.play('hit');
    return;
  }
  g.sendScout(q, r);
  sfx.play('jump');
  play?.toast('A scout sets out. They will be home by dawn — and off the tools tonight.');
  play?.refresh();
}

function doEndDay(): void {
  const g = game;
  if (!g || !actionGate()) return;

  g.events.length = 0;
  g.endDay();
  narrateNight(g, g.events);
  g.events.length = 0;

  play?.refresh();
  race?.ping(g.day, g.pop, g.liveScore());

  if (g.over) finishRun(g);
}

/** Turn the night's event stream into things you can see and hear. */
function narrateNight(g: Game, events: readonly Ev[]): void {
  let produced = 0;
  for (const e of events) {
    if (e.k === 'produce') {
      produced += e.amount;
      renderer?.float(e.q, e.r, `+${e.amount}`, FLOAT_COLOURS[e.res]);
    } else if (e.k === 'scoutBack') {
      renderer?.puff(e.q, e.r, PALETTE.scout, 14);
      if (e.found) {
        const what =
          e.found.kind === 'survivor' ? `+${e.found.amount} survivor` : `+${e.found.amount} ${e.found.kind}`;
        renderer?.float(e.q, e.r, what, PALETTE.cache);
        play?.toast(`A scout came home with ${what.replace('+', '')}.`, 'good');
        sfx.play('coin');
      } else {
        play?.toast('A scout came home with nothing but frostbite.');
      }
    } else if (e.k === 'feed' && e.starved > 0) {
      play?.toast(`${e.starved} starved — you are short of fish.`, 'bad');
      renderer?.shake(9);
      sfx.play('lose');
    } else if (e.k === 'shelter' && e.froze > 0) {
      play?.toast(`${e.froze} froze — not enough shelter inside the ring.`, 'bad');
      renderer?.shake(9);
      sfx.play('lose');
    } else if (e.k === 'cold') {
      renderer?.pulse(true);
    }
  }
  if (produced > 0) sfx.play('coin');
  if (!g.over) {
    const ring = g.warmth;
    renderer?.shake(3);
    play?.toast(`Night falls. The ring closes to ${ring}.`);
  }
}

function finishRun(g: Game): void {
  const summary = g.summary();
  lastSummary = summary;
  sfx.play(summary.won ? 'win' : 'lose');

  const key = bestKey(g.mode);
  if (summary.score > store.get(key, 0)) store.set(key, summary.score);

  if (race) {
    race.finish(summary);
    // The results screen appears when the ladder settles; until then the player
    // watches the standings fill in, which is the good part of a race.
    if (!race.settled()) {
      showResults();
      return;
    }
  }
  showResults();
}

// ── results ─────────────────────────────────────────────────────────────────

function showResults(): void {
  const summary = lastSummary;
  const g = game;
  if (!summary || !g) return;

  stopLoop();
  setPlaying(false);

  const multiplayer = !!race;
  const state = rounds?.state();
  const votes = state
    ? state.votes.length
      ? `Ready: ${state.votes.map((v) => v.name).join(', ')} (${state.votes.length}/${state.present.length})`
      : 'Nobody has hit play again yet.'
    : '';

  renderResults(shell.view, {
    mode: g.mode,
    summary,
    best: store.get(bestKey(g.mode), 0),
    standings: race?.standings() ?? [],
    closesInMs: race?.closesInMs() ?? null,
    multiplayer,
    votes,
    startsInMs: state?.startsInMs ?? null,
    canForceStart: !!state?.isHost && !!state?.canStart,
    onAgain: () => {
      if (rounds) {
        rounds.finish();
        rounds.vote();
        showResults();
      } else {
        startSolo(newSeed());
      }
    },
    onLobby: () => {
      // Back to the lobby does NOT leave the room.
      lastSummary = null;
      rounds?.finish();
      showLobby();
    },
    onMenu: showMenu,
    onShare: () => void shareRun(g, summary),
    onForceStart: () => rounds?.go(),
    onFeedback: (from) => (window as any).feedback?.open({ returnFocusTo: from }),
  });

  // Keep the countdowns on this screen honest without a rAF (which a
  // backgrounded tab freezes).
  if (!resultsTimer && multiplayer) {
    resultsTimer = setInterval(() => {
      if (lastSummary) showResults();
      else if (resultsTimer) {
        clearInterval(resultsTimer);
        resultsTimer = undefined;
      }
    }, 500);
  }
}

async function shareRun(g: Game, summary: Summary): Promise<void> {
  const url = new URL(location.href);
  url.search = '';
  url.searchParams.set('seed', String(g.seed));
  url.searchParams.set('mode', g.mode.id);
  const text = `I scored ${summary.score} on this Lastlight map (${g.mode.name}). Same map, your turn.`;
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Lastlight', text, url: url.toString() });
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url.toString()}`);
    play?.toast('Link copied.', 'good');
  } catch {
    // Share sheets get dismissed and clipboards get blocked; neither is an error
    // worth shouting about, but the player still needs the link.
    window.prompt('Copy this link:', `${text} ${url.toString()}`);
  }
}

// ── boot ────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();

  // Must run before ANY mesh — including presence/board meshes — because
  // Trystero builds one global connection pool from the first joinRoom's
  // config. Session-cached and fail-open, so it can never block a join.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    // Fails open to STUN-only; TURN is an upgrade, never a dependency.
  }

  const params = new URLSearchParams(location.search);
  const room = params.get('room');
  const seed = params.get('seed');
  const linkMode = params.get('mode');

  if (room) {
    // A deep-linked invite is honoured ONCE. `enterRoom` re-stamps the URL, and
    // walking back to the menu clears it, so a reload can never drag someone
    // into a room they already left.
    await enterRoom(room, false);
    return;
  }

  if (seed) {
    const n = Number(seed);
    if (Number.isFinite(n)) {
      mode = modeOf(linkMode);
      startSolo(n >>> 0);
      return;
    }
  }

  showMenu();
}

window.addEventListener('beforeunload', () => {
  void net?.leave();
});

// A first gesture anywhere unlocks audio; browsers block it until then.
const unlock = (): void => sfx.unlock();
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

void boot();

// Referenced so the mode list is reachable from the console for debugging and
// so a tree-shake can never drop a mode the wire might name.
export { MODES };
export type { Standing };
